/**
 * کارتِ «اعلانِ مرورگر را روشن کن؟».
 *
 * ═══ ⚠ چرا یک کارت، و نه مستقیم پنجره‌ی خودِ مرورگر ═══
 *
 * مرورگر اجازه‌ی پرسیدن را **یک بار** می‌دهد. کسی که آن پنجره را بی‌مقدمه
 * ببیند معمولاً «مسدود کن» می‌زند، و از آن لحظه هیچ کدی دیگر نمی‌تواند
 * بپرسد — نه امروز، نه هرگز. پس اول این‌جا می‌گوییم چرا، و پنجره‌ی مرورگر
 * فقط بعد از یک «بله»ی آگاهانه باز می‌شود.
 *
 * ═══ ⚠ و چرا خودمان می‌گوییم اپ بهتر است ═══
 *
 * چون هست: اعلانِ مرورگر با بسته شدنِ مرورگر می‌خوابد، و روی آیفون بدونِ
 * افزودن به صفحه‌ی خانه اصلاً وجود ندارد. کارتی که این را نگوید، کاربر را
 * به راهِ ضعیف‌تر می‌فرستد و خودش هم می‌داند.
 *
 * شرطِ نمایش در `core/lib/webPushOffer.ts` است و آزموده می‌شود.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { notifPrefsRepo } from '@/core/data/notifPrefsRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { shouldOfferPush, type PushPermission } from '@/core/lib/webPushOffer';
import { Card, Note } from '@/ui/kit';
import { StoreBadge } from '@/ui/StoreBadge';
import { enableWebPush, pushConfigured, pushPermission } from './webPush';
import { readSnooze, snoozeForever, snoozeOneWeek } from './pushSnooze';
import { prefLogRepo } from '@/core/data/prefLogRepo';

export function PushOfferCard({ deliberate = false }: { deliberate?: boolean } = {}) {
  useLocale();
  const { profile } = useSession();
  const [permission, setPermission] = React.useState<PushPermission>(() => pushPermission());
  const [snoozedUntil, setSnoozedUntil] = React.useState<number | null>(() => readSnooze());
  const [busy, setBusy] = React.useState(false);

  /**
   * ⚠ فقط وقتی پرسیده می‌شود که همه‌ی شرط‌های دیگر برقرار باشند — وگرنه
   * هر بار باز شدنِ صفحه یک کوئریِ بی‌مصرف بود. `enabled` همین را می‌گوید.
   */
  const native = useQuery({
    queryKey: ['hasNativeToken', profile?.id],
    queryFn: () => notifPrefsRepo.hasNativeToken(profile!.id),
    enabled: !!profile && pushConfigured() && permission === 'default',
    staleTime: 60 * 60_000,
  });

  /**
   * قرارِ WHOSUP-71 (تصمیمِ ۲): پیشنهاد نه سرِ اولین ورود، بلکه بعد از
   * اولین سیگنال/دوستی. `deliberate` یعنی صفحه‌ی تنظیماتِ اعلان‌ها — رفتن
   * به آن‌جا خودش قصد است و این شرط (و کوئری‌اش) را رد می‌کند.
   */
  const activity = useQuery({
    queryKey: ['hasFirstActivity', profile?.id],
    queryFn: () => notifPrefsRepo.hasFirstActivity(profile!.id),
    enabled: !deliberate && !!profile && pushConfigured() && permission === 'default',
    staleTime: 60 * 60_000,
  });

  // تا وقتی جوابِ «اپ را دارد؟» (و «قدمی برداشته؟») نیامده چیزی نشان
  // نمی‌دهیم: کارتی که بیاید و یک لحظه بعد ناپدید شود، از نیامدنش بدتر است.
  if (native.isLoading || activity.isLoading) return null;

  const offer = shouldOfferPush({
    signedIn: !!profile,
    permission,
    hasNativeToken: native.data ?? false,
    hasFirstActivity: deliberate || (activity.data ?? false),
    snoozedUntil,
    now: Date.now(),
    configured: pushConfigured(),
  });

  // «مسدود شد» تنها حالتی است که بعد از کلیکِ کاربر هم باید حرفی بزنیم:
  // او همین حالا دکمه را زد و باید بداند چرا هیچ اتفاقی نیفتاد.
  if (!offer) {
    return permission === 'denied' && snoozedUntil === null
      ? <Note tone="warn">{t('pushWebBlocked')}</Note>
      : null;
  }

  /**
   * ثبتِ تصمیم — روی سطرِ همین مرورگر و در تاریخچه (مایگریشن ۰۱۱۱).
   *
   * ⚠ خودِ تصمیم همان‌جا که بود می‌ماند: مجوز مالِ مرورگر است و «بعداً» در
   * `localStorage` می‌نشیند (دلیلش بالای pushSnooze.ts). این‌جا فقط رونوشت
   * می‌رود بالا، تا بشود گفت این آدم چند مرورگر دارد و آخری‌شان اجازه داده
   * یا نه. بی‌صدا شکست می‌خورد؛ یک یادداشتِ گمشده نباید جلوی روشن شدنِ
   * اعلان را بگیرد.
   */
  const note = (p: PushPermission | 'snoozed') => {
    // ⚠ `unsupported` به `unknown` نگاشته می‌شود و اختیاری نیست: قیدِ ستونِ
    // `push_permission` (مایگریشن ۰۱۱۱) آن مقدار را ندارد، و بدونِ این
    // نگاشت هر سطرِ سافاریِ آیفون بی‌صدا رد می‌شد — یعنی دقیقاً همان
    // دستگاه‌هایی که دانستنشان بیشترین ارزش را دارد.
    prefLogRepo.setPushPermission(p === 'unsupported' ? 'unknown' : p)
      .catch(() => undefined);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const decision = await enableWebPush(profile!.id);
      setPermission(decision);
      note(decision);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h3>{t('pushWebTitle')}</h3>
      <p className="dim">{t('pushWebWhy')}</p>
      {/* پیشنهادِ بهتر، پایین‌تر از خودِ پیشنهاد ولی قبل از دکمه‌ها:
          کسی که تا این‌جا خوانده، باید هر دو راه را دیده باشد. و فقط حرف
          نه — راهِ نصب همین‌جاست: دو فروشگاه، و روی دسکتاپ همان QRِ
          همیشگی (StoreBadge خودش تصمیم می‌گیرد QR لازم است یا نه). */}
      <Note tone="brand">{t('pushWebAppBetter')}</Note>
      <StoreBadge />
      <div className="chiprow chips-wrap">
        <button type="button" className="btn fill" disabled={busy} onClick={enable}>
          {busy ? '…' : t('pushWebEnable')}
        </button>
        <button type="button" className="btn"
          onClick={() => { setSnoozedUntil(snoozeOneWeek()); note('snoozed'); }}>
          {t('pushWebLater')}
        </button>
        <button type="button" className="btn"
          onClick={() => { setSnoozedUntil(snoozeForever()); note('denied'); }}>
          {t('pushWebNo')}
        </button>
      </div>
    </Card>
  );
}
